import React, { useState } from 'react';
import {
    Text,
    useTheme,
    DefaultButton,
    IconButton,
    TextField,
} from '@fluentui/react';
import { ComponentDetailsResponse } from '../models/CloudStatusApiModels';
import { isMobileWidth } from '../utils/StatusHelpers';
import '../styles/CommonStyles.css';

interface ComponentDetailsTabProps {
    componentDetails: ComponentDetailsResponse;
}

export const ComponentDetailsTab: React.FC<ComponentDetailsTabProps> = ({
    componentDetails,
}) => {
    const theme = useTheme();
    const isMobile = isMobileWidth();
    const [isEditingContext, setIsEditingContext] = useState(false);
    const [editedContext, setEditedContext] = useState<Record<string, string>>(
        {}
    );

    const handleActionClick = (endpoint?: string, httpMethod?: string) => {
        // TODO: Implement action handling
        console.log(`Action clicked: ${endpoint} (${httpMethod})`);
    };

    const handleEditContext = () => {
        setEditedContext({ ...componentDetails.context });
        setIsEditingContext(true);
    };

    const handleSaveContext = () => {
        // TODO: Implement save context to API
        console.log('Saving context:', editedContext);
        setIsEditingContext(false);
    };

    const handleCancelEdit = () => {
        setIsEditingContext(false);
        setEditedContext({});
    };

    return (
        <div>
            {/* Description Section - Moved to top */}
            {componentDetails.description && (
                <div style={{ marginBottom: '24px' }}>
                    <Text
                        variant="large"
                        styles={{
                            root: {
                                fontWeight: 600,
                                color: theme.palette.themePrimary,
                                marginBottom: '12px',
                                display: 'block',
                            },
                        }}
                    >
                        Description
                    </Text>
                    <div
                        style={{
                            padding: '12px',
                            backgroundColor: theme.palette.neutralLighter,
                            border: `1px solid ${theme.palette.neutralLight}`,
                            borderRadius: '4px',
                        }}
                    >
                        <Text variant="medium">
                            {componentDetails.description}
                        </Text>
                    </div>
                </div>
            )}

            {/* Component Identity Section */}
            <div style={{ marginBottom: '24px' }}>
                <Text
                    variant="large"
                    styles={{
                        root: {
                            fontWeight: 600,
                            color: theme.palette.themePrimary,
                            marginBottom: '12px',
                            display: 'block',
                        },
                    }}
                >
                    Identity
                </Text>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: '16px',
                    }}
                >
                    <div>
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.neutralSecondary,
                                    display: 'block',
                                    marginBottom: '4px',
                                },
                            }}
                        >
                            Component GUID
                        </Text>
                        <div
                            style={{
                                padding: '8px 12px',
                                backgroundColor: theme.palette.neutralLighter,
                                border: `1px solid ${theme.palette.neutralLight}`,
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                            }}
                        >
                            {componentDetails.componentGuid}
                        </div>
                    </div>

                    <div>
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.neutralSecondary,
                                    display: 'block',
                                    marginBottom: '4px',
                                },
                            }}
                        >
                            Service Category
                        </Text>
                        <div
                            style={{
                                padding: '8px 12px',
                                backgroundColor: theme.palette.neutralLighter,
                                border: `1px solid ${theme.palette.neutralLight}`,
                                borderRadius: '4px',
                            }}
                        >
                            {componentDetails.serviceCategory}
                        </div>
                    </div>

                    <div>
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.neutralSecondary,
                                    display: 'block',
                                    marginBottom: '4px',
                                },
                            }}
                        >
                            Pipeline Stage
                        </Text>
                        <div
                            style={{
                                padding: '8px 12px',
                                backgroundColor: theme.palette.neutralLighter,
                                border: `1px solid ${theme.palette.neutralLight}`,
                                borderRadius: '4px',
                            }}
                        >
                            {componentDetails.pipelineStage}
                        </div>
                    </div>

                    {componentDetails.cloudRegion && (
                        <div>
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        color: theme.palette.neutralSecondary,
                                        display: 'block',
                                        marginBottom: '4px',
                                    },
                                }}
                            >
                                Cloud Region
                            </Text>
                            <div
                                style={{
                                    padding: '8px 12px',
                                    backgroundColor:
                                        theme.palette.neutralLighter,
                                    border: `1px solid ${theme.palette.neutralLight}`,
                                    borderRadius: '4px',
                                }}
                            >
                                {componentDetails.cloudRegion}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Configuration Section */}
            <div style={{ marginBottom: '24px' }}>
                <Text
                    variant="large"
                    styles={{
                        root: {
                            fontWeight: 600,
                            color: theme.palette.themePrimary,
                            marginBottom: '12px',
                            display: 'block',
                        },
                    }}
                >
                    Configuration
                </Text>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: '16px',
                    }}
                >
                    {componentDetails.executionIntervalMinutes !==
                        undefined && (
                        <div>
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        color: theme.palette.neutralSecondary,
                                        display: 'block',
                                        marginBottom: '4px',
                                    },
                                }}
                            >
                                Execution Interval
                            </Text>
                            <div
                                style={{
                                    padding: '8px 12px',
                                    backgroundColor:
                                        theme.palette.neutralLighter,
                                    border: `1px solid ${theme.palette.neutralLight}`,
                                    borderRadius: '4px',
                                }}
                            >
                                {componentDetails.executionIntervalMinutes}{' '}
                                minutes
                            </div>
                        </div>
                    )}

                    <div>
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.neutralSecondary,
                                    display: 'block',
                                    marginBottom: '4px',
                                },
                            }}
                        >
                            Status
                        </Text>
                        <div
                            style={{
                                padding: '8px 12px',
                                backgroundColor: theme.palette.neutralLighter,
                                border: `1px solid ${theme.palette.neutralLight}`,
                                borderRadius: '4px',
                                color: componentDetails.enabled
                                    ? theme.palette.green
                                    : theme.palette.redDark,
                                fontWeight: 600,
                            }}
                        >
                            {componentDetails.enabled
                                ? '? Enabled'
                                : '? Disabled'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Context Variables Section */}
            {componentDetails.context &&
                Object.keys(componentDetails.context).length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px',
                            }}
                        >
                            <Text
                                variant="large"
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        color: theme.palette.themePrimary,
                                    },
                                }}
                            >
                                Context Variables
                            </Text>
                            {!isEditingContext ? (
                                <IconButton
                                    iconProps={{ iconName: 'Edit' }}
                                    title="Edit context variables"
                                    ariaLabel="Edit context variables"
                                    onClick={handleEditContext}
                                    styles={{
                                        root: {
                                            color: theme.palette.themePrimary,
                                        },
                                    }}
                                />
                            ) : (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <DefaultButton
                                        text="Cancel"
                                        onClick={handleCancelEdit}
                                        styles={{ root: { minWidth: '70px' } }}
                                    />
                                    <DefaultButton
                                        text="Save"
                                        onClick={handleSaveContext}
                                        primary
                                        styles={{ root: { minWidth: '70px' } }}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile
                                    ? '1fr'
                                    : '1fr 1fr',
                                gap: '16px',
                            }}
                        >
                            {Object.entries(
                                isEditingContext
                                    ? editedContext
                                    : componentDetails.context
                            ).map(([key, value]) => (
                                <div key={key}>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                fontWeight: 600,
                                                color: theme.palette
                                                    .neutralSecondary,
                                                display: 'block',
                                                marginBottom: '4px',
                                            },
                                        }}
                                    >
                                        {key}
                                    </Text>
                                    {isEditingContext ? (
                                        <TextField
                                            value={value}
                                            onChange={(_, newValue) => {
                                                setEditedContext((prev) => ({
                                                    ...prev,
                                                    [key]: newValue || '',
                                                }));
                                            }}
                                            multiline
                                            autoAdjustHeight
                                            styles={{
                                                field: {
                                                    fontFamily: 'monospace',
                                                    fontSize: '12px',
                                                },
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                padding: '8px 12px',
                                                backgroundColor:
                                                    theme.palette.white,
                                                border: `1px solid ${theme.palette.neutralLight}`,
                                                borderRadius: '4px',
                                                fontFamily: 'monospace',
                                                fontSize: '12px',
                                                overflowWrap: 'break-word',
                                            }}
                                        >
                                            {value}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            {/* Actions Section */}
            {componentDetails.availableActions &&
                componentDetails.availableActions.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <Text
                            variant="large"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.themePrimary,
                                    marginBottom: '12px',
                                    display: 'block',
                                },
                            }}
                        >
                            Available Actions
                        </Text>
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '12px',
                            }}
                        >
                            {componentDetails.availableActions.map((action) => (
                                <DefaultButton
                                    key={action.actionId}
                                    text={action.displayName}
                                    iconProps={
                                        action.iconName
                                            ? { iconName: action.iconName }
                                            : undefined
                                    }
                                    title={action.description}
                                    onClick={() =>
                                        handleActionClick(
                                            action.endpoint,
                                            action.httpMethod
                                        )
                                    }
                                    styles={{
                                        root: {
                                            minWidth: isMobile
                                                ? '100%'
                                                : '150px',
                                        },
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
        </div>
    );
};
